import React from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle,
  ArrowRight,
  Cpu,
  Shield,
  Users,
  Award,
} from "lucide-react";
import AnimatedSection from "../components/common/AnimatedSection";
import SectionHeading from "../components/common/SectionHeading";
import Button from "../components/common/Button";
import { useCarousel } from "../hooks/useCarousel";
import factoryImg from "../assets/images/factory.jpeg";
import CNCIMG from "../assets/images/CNC.jpg";
import EDGEIMG from "../assets/images/EDGE.jpg";
import PANELIMG from "../assets/images/PANEL.jpeg";
import DRILLIMG from "../assets/images/DRILL.webp";

const machines = [
  {
    name: "CNC Router",
    brand: "Biesse",
    description:
      "Precision cutting for complex shapes and patterns with sub-millimeter accuracy.",
    image: CNCIMG,
  },
  {
    name: "Edge Bander",
    brand: "Biesse",
    description:
      "Seamless edge finishing that protects panels and enhances aesthetics.",
    image: EDGEIMG,
  },
  {
    name: "Panel Saw",
    brand: "Biesse",
    description: "High-speed precision cutting for large panel processing.",
    image: PANELIMG,
  },
  {
    name: "Drilling Machine",
    brand: "Biesse",
    description:
      "Multi-point drilling for hardware installation with perfect alignment.",
    image: DRILLIMG,
  },
];

const comparison = [
  {
    feature: "Precision",
    carpentry: "Manual measurement, human error",
    outsourced: "Varies by vendor",
    iravatham: "Sub-mm accuracy with Biesse CNC",
  },
  {
    feature: "Materials",
    carpentry: "Often MDF or low-grade ply",
    outsourced: "Inconsistent quality",
    iravatham: "MR/BWR/BWP Ply & HDHMR only",
  },
  {
    feature: "Timeline",
    carpentry: "45-60 days, often delayed",
    outsourced: "30-45 days",
    iravatham: "20 days for modular",
  },
  {
    feature: "Quality Control",
    carpentry: "Minimal or none",
    outsourced: "Basic checks",
    iravatham: "Multi-point inspection",
  },
  {
    feature: "Warranty",
    carpentry: "Verbal assurance",
    outsourced: "1-3 years",
    iravatham: "10 years on materials",
  },
  {
    feature: "Design Support",
    carpentry: "Limited",
    outsourced: "Template-based",
    iravatham: "Full 3D visualization",
  },
];

const features = [
  {
    icon: Cpu,
    title: "German Machinery",
    description:
      "Biesse machines from Italy ensure every cut, edge, and drill is perfect.",
  },
  {
    icon: Shield,
    title: "Quality Materials",
    description: "Only MR, BWR, BWP Ply and HDHMR. No MDF, no compromises.",
  },
  {
    icon: Users,
    title: "Dedicated Teams",
    description:
      "Separate teams for Design, Production, Quality, and Installation.",
  },
  {
    icon: Award,
    title: "10-Year Warranty",
    description: "Confidence in our craft, backed by a decade-long warranty.",
  },
];

export default function FactoryPage() {
  const { emblaRef, scrollPrev, scrollNext, canScrollPrev, canScrollNext } =
    useCarousel({ loop: true });

  return (
    <div>
      {/* Hero */}
      <section className="relative py-32 lg:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={factoryImg}
            alt="Iravatham Factory"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/95 via-brand-primary/80 to-brand-primary/60" />
        </div>
        <div className="container-custom section-padding relative">
          <AnimatedSection className="max-w-3xl text-white">
            <span className="text-brand-accent text-sm font-medium uppercase tracking-wider">
              Our Manufacturing
            </span>
            <h1 className="font-heading text-5xl mt-4 mb-6 text-white">
              Inside our 10,000+ sq. ft factory.
            </h1>
            <p className="text-xl text-white/80 leading-relaxed mb-8">
              Biesse machines, MR/BWR/BWP Ply, HDHMR boards, and a dedicated
              team – everything under one roof for factory-fit precision.
            </p>
            <Button to="/contact" variant="primary" size="large">
              Book a Factory Tour
            </Button>
          </AnimatedSection>
        </div>
      </section>

      {/* Factory Process Video */}
      <section className="section-spacing bg-background-main">
        <div className="container-custom section-padding">
          <div className="relative w-full aspect-video overflow-hidden bg-black rounded-3xl">
            <iframe
              src="https://player.vimeo.com/video/1145089677?autoplay=1&loop=1&muted=0&controls=1&title=0&byline=0&portrait=0"
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title="Iravatham Factory Process"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-spacing bg-white">
        <div className="container-custom section-padding">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <AnimatedSection key={feature.title} delay={index * 100}>
                <div className="text-center">
                  <div className="w-16 h-16 bg-brand-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-8 h-8 text-brand-accent" />
                  </div>
                  <h3 className="font-heading text-xl text-brand-primary mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-text-secondary">{feature.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="section-spacing bg-background-main">
        <div className="container-custom section-padding">
          <SectionHeading
            label="Why Factory Matters"
            title="The Iravatham difference, compared."
            align="center"
          />

          <AnimatedSection>
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-2xl shadow-soft overflow-hidden">
                <thead>
                  <tr className="border-b border-border-light">
                    <th className="px-6 py-4 text-left font-heading text-brand-primary">
                      Feature
                    </th>
                    <th className="px-6 py-4 text-left text-text-secondary">
                      Local Carpentry
                    </th>
                    <th className="px-6 py-4 text-left text-text-secondary">
                      Outsourced Workshop
                    </th>
                    <th className="px-6 py-4 text-left text-brand-accent font-medium">
                      Iravatham
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row, index) => (
                    <tr
                      key={row.feature}
                      className={
                        index < comparison.length - 1
                          ? "border-b border-border-light"
                          : ""
                      }
                    >
                      <td className="px-6 py-4 font-medium text-brand-primary">
                        {row.feature}
                      </td>
                      <td className="px-6 py-4 text-text-secondary">
                        {row.carpentry}
                      </td>
                      <td className="px-6 py-4 text-text-secondary">
                        {row.outsourced}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-2 text-brand-accent font-medium">
                          <CheckCircle className="w-4 h-4" />
                          {row.iravatham}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Machines Carousel */}
      <section className="section-spacing bg-white">
        <div className="container-custom section-padding">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <SectionHeading
              label="Our Equipment"
              title="Precision machinery for perfect results."
              align="left"
              className="mb-0"
            />
            <div className="flex items-center gap-3">
              <button
                onClick={scrollPrev}
                disabled={!canScrollPrev}
                className="w-12 h-12 rounded-full border-2 border-border-light flex items-center justify-center text-brand-primary hover:bg-brand-primary hover:text-white hover:border-brand-primary transition-all disabled:opacity-30"
              >
                <ArrowRight className="w-5 h-5 rotate-180" />
              </button>
              <button
                onClick={scrollNext}
                disabled={!canScrollNext}
                className="w-12 h-12 rounded-full border-2 border-border-light flex items-center justify-center text-brand-primary hover:bg-brand-primary hover:text-white hover:border-brand-primary transition-all disabled:opacity-30"
              >
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6">
              {machines.map((machine) => (
                <div
                  key={machine.name}
                  className="flex-none w-[80%] md:w-[45%] lg:w-[30%]"
                >
                  <div className="bg-background-card rounded-2xl overflow-hidden">
                    <div className="aspect-[4/3]">
                      <img
                        src={machine.image}
                        alt={machine.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-6">
                      <span className="text-brand-accent text-sm font-medium">
                        {machine.brand}
                      </span>
                      <h4 className="font-heading text-xl text-brand-primary mt-1 mb-2">
                        {machine.name}
                      </h4>
                      <p className="text-text-secondary text-sm">
                        {machine.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* QC Process */}
      <section className="section-spacing bg-background-main">
        <div className="container-custom section-padding">
          <SectionHeading
            label="Quality Control"
            title="Every panel passes through 5 checkpoints."
            align="center"
          />

          <div className="grid md:grid-cols-5 gap-4">
            {[
              { step: "Cut", desc: "CNC precision cutting" },
              { step: "Edge-band", desc: "Seamless edge finishing" },
              { step: "Drill", desc: "Hardware preparation" },
              { step: "Assemble", desc: "Pre-assembly check" },
              { step: "Inspect", desc: "Final quality sign-off" },
            ].map((item, index) => (
              <AnimatedSection key={item.step} delay={index * 100}>
                <div className="relative">
                  <div className="bg-white rounded-2xl p-6 text-center shadow-soft">
                    <div className="w-12 h-12 bg-brand-accent rounded-full flex items-center justify-center mx-auto mb-4 text-white font-heading text-lg">
                      {index + 1}
                    </div>
                    <h4 className="font-heading text-lg text-brand-primary mb-1">
                      {item.step}
                    </h4>
                    <p className="text-text-secondary text-sm">{item.desc}</p>
                  </div>
                  {index < 4 && (
                    <ArrowRight className="hidden md:block absolute top-1/2 -right-5 w-6 h-6 text-brand-accent/30 -translate-y-1/2" />
                  )}
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-brand">
        <div className="container-custom section-padding text-center">
          <AnimatedSection>
            <h2 className="font-heading text-h2 text-white mb-6">
              See our factory in action.
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10">
              Book a factory tour and witness the precision that goes into every
              Iravatham interior.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button to="/contact" variant="white" size="large">
                Book Factory Tour
              </Button>
              <Button to="/process" variant="outline" size="large">
                View Our Process
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
